import { Box, Button, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { RestaurantLocation } from "../locations";

type LocationProps = {
  location: RestaurantLocation;
};

export const locationImageSx = (location: RestaurantLocation) => ({
  position: "absolute" as const,
  inset: 0,
  zIndex: -2,
  backgroundImage: `url(${location.image})`,
  backgroundSize: "cover",
  backgroundPosition: location.imagePosition ?? "center",
  transition: "transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)",
});

export const LocationCardMedia = ({ location }: LocationProps) => {
  const gallery = location.gallery ?? [];
  if (gallery.length < 2) {
    return (
      <Box className="location-card-image" role="img" aria-label={location.imageAlt} sx={locationImageSx(location)} />
    );
  }

  const hasHero = gallery.length >= 5;
  const tiles = hasHero ? gallery.slice(0, 5) : gallery.slice(0, 4);

  return (
    <Box
      className="location-card-image"
      role="img"
      aria-label={location.imageAlt}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: -2,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: hasHero ? "1.15fr 1fr 1fr" : "1fr 1fr",
        gap: "2px",
        transition: "transform 0.8s cubic-bezier(0.2, 0.8, 0.2, 1)",
      }}
    >
      {tiles.map((photo, index) => (
        <Box
          key={photo.src}
          sx={{
            gridColumn: hasHero && index === 0 ? "1 / -1" : "auto",
            backgroundImage: `url(${photo.src})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      ))}
    </Box>
  );
};

export const locationGradientSx = {
  position: "absolute",
  inset: 0,
  zIndex: -1,
  background:
    "linear-gradient(180deg, rgba(32, 20, 30, 0.35) 0%, rgba(32, 20, 30, 0.25) 25%, rgba(32, 20, 30, 0.7) 55%, rgba(32, 20, 30, 0.97) 100%)",
};

export const LocationServiceChip = ({ location }: LocationProps) => (
  <Box
    sx={{
      alignSelf: "flex-start",
      px: 1.75,
      py: 0.75,
      borderRadius: "999px",
      fontSize: { xs: "0.8rem", md: "0.85rem" },
      fontWeight: 800,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "white",
      backgroundColor: "rgba(32, 20, 30, 0.82)",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
    }}
  >
    {location.service}
  </Box>
);

export const LocationName = ({ location, component = "h3" }: LocationProps & { component?: "h2" | "h3" }) => (
  <Typography
    variant="h4"
    component={component}
    sx={{
      color: "white",
      fontWeight: 800,
      letterSpacing: "-0.02em",
      fontSize: { xs: "2rem", md: "2.4rem" },
      lineHeight: 1.1,
      mb: 1.5,
      textShadow: "0 2px 4px rgba(0, 0, 0, 0.6), 0 4px 18px rgba(0, 0, 0, 0.5)",
    }}
  >
    {location.name}
  </Typography>
);

const addressPillSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 1.25,
  px: 2,
  py: 1.25,
  borderRadius: "16px",
  color: "white",
  backgroundColor: "rgba(32, 20, 30, 0.72)",
  border: "1px solid rgba(255, 255, 255, 0.24)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
} as const;

export const LocationAddressPill = ({ location }: LocationProps) => {
  const contents = (
    <>
      <Box
        sx={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#FFE566",
          color: "#20141e",
        }}
      >
        <LocationOnIcon sx={{ fontSize: "1.25rem" }} />
      </Box>
      <Typography
        component="span"
        sx={{
          color: "white",
          fontWeight: 700,
          fontSize: { xs: "1rem", md: "1.125rem" },
          lineHeight: 1.35,
          letterSpacing: "0.01em",
        }}
      >
        {location.address}
      </Typography>
    </>
  );

  if (!location.mapsLink) {
    return <Box sx={addressPillSx}>{contents}</Box>;
  }

  return (
  <Link
    href={location.mapsLink}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`Directions to ${location.address}`}
    underline="none"
    sx={{
      ...addressPillSx,
      transition: "background-color 0.25s ease, border-color 0.25s ease",
      "&:hover": {
        backgroundColor: "rgba(32, 20, 30, 0.88)",
        borderColor: "#FFE566",
      },
    }}
  >
    {contents}
  </Link>
  );
};

const LocationCard = ({ location }: LocationProps) => (
  <Box
    component="article"
    sx={{
      position: "relative",
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: { xs: 300, md: 380 },
      borderRadius: "24px",
      overflow: "hidden",
      isolation: "isolate",
      boxShadow: "0 24px 48px -16px rgba(32, 20, 30, 0.55), 0 8px 16px -8px rgba(32, 20, 30, 0.35)",
      transition: "transform 0.35s ease, box-shadow 0.35s ease",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 32px 64px -18px rgba(32, 20, 30, 0.65), 0 12px 24px -10px rgba(32, 20, 30, 0.4)",
      },
      "&:hover .location-card-image": {
        transform: "scale(1.06)",
      },
    }}
  >
    <LocationCardMedia location={location} />
    <Box sx={locationGradientSx} />

    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: 3,
        p: { xs: 2.5, md: 3.5 },
        color: "white",
      }}
    >
      <LocationServiceChip location={location} />

      <Box>
        <LocationName location={location} />
        <Box sx={{ mb: 3 }}>
          <LocationAddressPill location={location} />
        </Box>
        <Button
          component={RouterLink}
          to={location.comingSoon ? location.statusPage || "/san-jose" : `/menu?location=${location.id}`}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{
            px: 3,
            py: 1.25,
            borderRadius: "999px",
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 700,
            color: location.comingSoon ? "white" : "#20141e",
            backgroundColor: location.comingSoon ? "#8a8a8a" : "#FFE566",
            boxShadow: "0 8px 20px -6px rgba(0, 0, 0, 0.45)",
            "& .MuiButton-endIcon": { transition: "transform 0.25s ease" },
            "&:hover": {
              backgroundColor: location.comingSoon ? "#6f6f6f" : "#FFD84A",
              boxShadow: "0 10px 24px -6px rgba(0, 0, 0, 0.55)",
            },
            "&:hover .MuiButton-endIcon": { transform: "translateX(4px)" },
          }}
        >
          {location.comingSoon ? "Permit pending" : `Order from ${location.name}`}
        </Button>
      </Box>
    </Box>
  </Box>
);

export default LocationCard;
